import React, { useRef, useState } from 'react'
import { useId } from '@reach/auto-id'
import { Button } from 'components/ui'
import { DockTooltip } from './DockTooltip'
import { useCreationText } from '../../hooks/useCreationText'
import { useSubtaskMenu } from '../../hooks/useSubtaskMenu'
import { subtaskTypeKeys } from '../../model/subtaskTypes'
import { getDockMagnification } from './magnification'
import styles from './styles.module.css'

interface ISubtaskDockProps {
  buttons: ReturnType<typeof useSubtaskMenu>['buttons']
  existingTypes: number[]
  onAdd: (type: number) => void
  disabled: boolean
  rtl: boolean
  hint?: React.ReactNode
  appearance?: 'light'
}

export function SubtaskDock({ buttons, existingTypes, onAdd, disabled, rtl, hint, appearance }: ISubtaskDockProps) {
  const t = useCreationText()
  const [pointer, setPointer] = useState<number | null>(null)
  const [focused, setFocused] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState<number | null>(null)
  const itemsRef = useRef<HTMLDivElement>(null)
  const tooltipId = `subtask-dock-tooltip-${useId()}`

  const active = disabled ? null : pointer ?? focused
  const magnification = getDockMagnification(buttons.length, active)
  const activeIndex =
    active === null || !buttons.length ? null : Math.max(0, Math.min(Math.round(active), buttons.length - 1))
  const tooltipIndex = activeIndex === dismissed ? null : activeIndex
  const tooltipLabel = activeIndex === null ? '' : t[subtaskTypeKeys[buttons[activeIndex].type]]

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || disabled) return

    setFocused(null)

    const slots = Array.from(event.currentTarget.children).filter((node) => node.hasAttribute('data-dock-slot'))
    if (!slots.length) return

    const first = slots[0].getBoundingClientRect()
    const last = slots[slots.length - 1].getBoundingClientRect()
    const firstCenter = first.left + first.width / 2
    const lastCenter = last.left + last.width / 2
    const span = lastCenter - firstCenter

    const nextPointer = span ? ((event.clientX - firstCenter) / span) * (slots.length - 1) : 0
    const nextIndex = Math.max(0, Math.min(Math.round(nextPointer), slots.length - 1))

    setPointer(nextPointer)
    if (nextIndex !== dismissed) setDismissed(null)
  }

  const handlePointerLeave = () => {
    setPointer(null)
    setDismissed(null)
  }

  return (
    <div
      className={`creation-list-actions ${styles.position}`}
      style={{ '--dock-item-count': buttons.length } as React.CSSProperties}
    >
      {hint && <div className={styles.hint}>{hint}</div>}
      <div className={styles.dockFrame}>
        <div className={styles.dock} data-appearance={appearance} role="group" aria-label={t.add}>
          <div
            className={styles.items}
            ref={itemsRef}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}
            onPointerCancel={handlePointerLeave}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation()
                setDismissed(activeIndex)
              }
            }}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) setFocused(null)
            }}
          >
            {buttons.map(({ type, Component }, index) => {
              const { scale, shift } = magnification[index]
              const label = t[subtaskTypeKeys[type]]
              const exists = existingTypes.includes(type)
              const indicatorId = `${tooltipId}-existing-${type}`

              return (
                <div
                  key={type}
                  className={styles.slot}
                  data-dock-slot
                  style={
                    {
                      '--dock-scale': scale,
                      '--dock-shift': `${shift * 100 * (rtl ? -1 : 1)}%`,
                    } as React.CSSProperties
                  }
                >
                  <Button
                    type="button"
                    variant="icon"
                    className={styles.action}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    aria-label={label}
                    aria-describedby={
                      [tooltipIndex === index && tooltipId, exists && indicatorId].filter(Boolean).join(' ') ||
                      undefined
                    }
                    isDisabled={disabled}
                    onClick={(event) => {
                      if (event.detail > 0) setFocused(null)
                      setDismissed(index)
                      onAdd(type)
                    }}
                    onFocus={() => {
                      setPointer(null)
                      setFocused(index)
                      setDismissed(null)
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Escape') {
                        setPointer(null)
                        setFocused(index)
                      }
                    }}
                  >
                    <Component width="100%" height="100%" iconWidth={22} iconHeight={22} />
                  </Button>
                  {exists && (
                    <span
                      id={indicatorId}
                      className={styles.indicator}
                      role="img"
                      aria-label={`${label}: ${t.alreadyAdded}`}
                    />
                  )}
                </div>
              )
            })}
            <DockTooltip id={tooltipId} label={tooltipLabel} index={tooltipIndex} itemsRef={itemsRef} />
          </div>
        </div>
      </div>
    </div>
  )
}
